/**
 * Grade Sync Admin Page
 * 
 * Trang quản lý đồng bộ điểm từ CSDL phụ
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Modal,
  Input,
  message,
  Alert,
  Spin,
  Tabs,
  List,
  Tooltip,
  Divider
} from 'antd';
import {
  SyncOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { GradeSyncService } from '../../../services/gradeSyncService';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface GradeTable {
  tableName: string;
  khoaHocId: string;
}

interface SyncResult {
  khoaHocId: string;
  success: boolean;
  totalRecords: number;
  syncedRecords: number;
  insertedRecords: number;
  updatedRecords: number;
  errors: string[];
  duration: number;
}

const GradeSyncPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [tables, setTables] = useState<GradeTable[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [syncingTable, setSyncingTable] = useState<string | null>(null);
  const [syncResults, setSyncResults] = useState<SyncResult[]>([]);
  const [syncAllProgress, setSyncAllProgress] = useState<{ current: number; total: number } | null>(null);
  const [studentMaHS, setStudentMaHS] = useState('');
  const [studentSyncing, setStudentSyncing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tablesRes, statsRes, historyRes] = await Promise.all([
        GradeSyncService.getGradeTables(),
        GradeSyncService.getGradeStatistics(),
        GradeSyncService.getSyncHistory(50)
      ]);

      if (tablesRes.success && tablesRes.data) {
        setTables(tablesRes.data);
      }
      if (statsRes.success && statsRes.data) {
        setStatistics(statsRes.data);
      }
      if (historyRes.success && historyRes.data) {
        setSyncResults(historyRes.data);
      }
    } catch (error) {
      message.error('Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTable = async (tableName: string) => {
    setSyncingTable(tableName);
    try {
      const result = await GradeSyncService.syncGradesFromTable(tableName);
      if (result.success && result.data) {
        message.success(result.message || 'Đồng bộ thành công');
        setSyncResults(prev => [...prev, result.data!]);
        loadData(); // Reload statistics
      } else {
        message.error(result.error || 'Đồng bộ thất bại');
      }
    } catch (error) {
      message.error('Lỗi khi đồng bộ');
    } finally {
      setSyncingTable(null);
    }
  };

  const handleSyncAll = async () => {
    Modal.confirm({
      title: 'Xác nhận đồng bộ tất cả',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Bạn có chắc muốn đồng bộ điểm từ tất cả {tables.length} bảng?</p>
          <p style={{ color: '#faad14' }}>
            <ExclamationCircleOutlined /> Quá trình này có thể mất vài phút.
          </p>
        </div>
      ),
      okText: 'Đồng bộ',
      cancelText: 'Hủy',
      onOk: async () => {
        setSyncAllProgress({ current: 0, total: tables.length });
        try {
          const result = await GradeSyncService.syncAllGrades();
          if (result.success && result.data) {
            message.success(
              `Đồng bộ hoàn tất: ${result.data.totalSynced} records (${result.data.totalInserted} mới, ${result.data.totalUpdated} cập nhật)`
            );
            setSyncResults(result.data.details);
            loadData();
          } else {
            message.error(result.error || 'Đồng bộ thất bại');
          }
        } catch (error) {
          message.error('Lỗi khi đồng bộ');
        } finally {
          setSyncAllProgress(null);
        }
      }
    });
  };

  const handleSyncStudent = async () => {
    if (!studentMaHS || studentMaHS.length < 5) {
      message.warning('Vui lòng nhập mã sinh viên hợp lệ');
      return;
    }

    setStudentSyncing(true);
    try {
      const result = await GradeSyncService.syncGradesForStudent(studentMaHS);
      if (result.success && result.data) {
        message.success(`Đồng bộ thành công ${result.data.syncedRecords} môn học cho sinh viên ${studentMaHS}`);
        loadData();
      } else {
        message.warning(result.error || 'Không tìm thấy dữ liệu điểm');
      }
    } catch (error) {
      message.error('Lỗi khi đồng bộ điểm sinh viên');
    } finally {
      setStudentSyncing(false);
    }
  };

  const tableColumns: ColumnsType<GradeTable> = [
    {
      title: 'Tên bảng',
      dataIndex: 'tableName',
      key: 'tableName',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Khóa học ID',
      dataIndex: 'khoaHocId',
      key: 'khoaHocId',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<SyncOutlined spin={syncingTable === record.tableName} />}
          onClick={() => handleSyncTable(record.tableName)}
          loading={syncingTable === record.tableName}
          disabled={syncingTable !== null && syncingTable !== record.tableName}
        >
          Đồng bộ
        </Button>
      )
    }
  ];

  const resultColumns: ColumnsType<any> = [
    {
      title: 'Thời gian đồng bộ',
      dataIndex: 'syncTime',
      key: 'syncTime',
      width: 180,
      render: (val: string) => (
        <Space>
          <ClockCircleOutlined style={{ color: '#1890ff' }} />
          <Text>{val}</Text>
        </Space>
      )
    },
    {
      title: 'Khóa học',
      dataIndex: 'khoaHocId',
      key: 'khoaHocId',
      width: 100,
      render: (val: string) => <Tag color="blue">{val}</Tag>
    },
    {
      title: 'Tổng records',
      dataIndex: 'totalRecords',
      key: 'totalRecords',
      width: 120,
      align: 'center',
      render: (val: number) => <Text strong>{val?.toLocaleString()}</Text>
    },
    {
      title: 'Số sinh viên',
      dataIndex: 'totalStudents',
      key: 'totalStudents',
      width: 120,
      align: 'center',
      render: (val: number) => (
        <Space>
          <UserOutlined />
          <Text>{val?.toLocaleString()}</Text>
        </Space>
      )
    },
    {
      title: 'Đạt',
      dataIndex: 'passedRecords',
      key: 'passedRecords',
      width: 80,
      align: 'center',
      render: (val: number) => <Tag color="success">{val?.toLocaleString()}</Tag>
    },
    {
      title: 'Không đạt',
      dataIndex: 'failedRecords',
      key: 'failedRecords',
      width: 100,
      align: 'center',
      render: (val: number) => (val && val > 0) ? <Tag color="error">{val}</Tag> : <Tag color="default">0</Tag>
    },
    {
      title: 'Điểm TB',
      dataIndex: 'avgGrade',
      key: 'avgGrade',
      width: 100,
      align: 'center',
      render: (val: number) => val ? (
        <Text type={val >= 5 ? 'success' : 'warning'} strong>
          {val.toFixed(2)}
        </Text>
      ) : '-'
    },
    {
      title: 'Trạng thái',
      dataIndex: 'success',
      key: 'success',
      width: 100,
      align: 'center',
      render: (val: boolean) => val !== false ? (
        <Tag color="success" icon={<CheckCircleOutlined />}>Đã đồng bộ</Tag>
      ) : (
        <Tag color="error" icon={<CloseCircleOutlined />}>Lỗi</Tag>
      )
    }
  ];

  // Render overview statistics
  const renderStatistics = () => {
    if (!statistics) return null;

    const { overview, gradeDistribution } = statistics;
    const passRate = overview.recordsWithGrades > 0
      ? ((overview.recordsWithGrades - overview.failedRecords) / overview.recordsWithGrades * 100).toFixed(1)
      : 0;

    return (
      <div>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Tổng records"
                value={overview.totalRecords}
                prefix={<DatabaseOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Số sinh viên"
                value={overview.totalStudents}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Số môn học"
                value={overview.totalSubjects}
                prefix={<BarChartOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Tỷ lệ đạt"
                value={passRate}
                suffix="%"
                valueStyle={{ color: Number(passRate) >= 80 ? '#52c41a' : '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col xs={24} md={12}>
            <Card title="Phân bố điểm chữ" size="small">
              <List
                dataSource={gradeDistribution}
                renderItem={(item: any) => (
                  <List.Item style={{ padding: '8px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Tag 
                        color={
                          item.DiemChu === 'A' ? 'green' :
                          item.DiemChu === 'B+' || item.DiemChu === 'B' ? 'blue' :
                          item.DiemChu === 'C+' || item.DiemChu === 'C' ? 'purple' :
                          item.DiemChu === 'D+' || item.DiemChu === 'D' ? 'orange' :
                          'red'
                        }
                        style={{ width: 40, textAlign: 'center' }}
                      >
                        {item.DiemChu}
                      </Tag>
                      <Progress 
                        percent={Math.round(item.count / overview.recordsWithGrades * 100)} 
                        size="small"
                        style={{ flex: 1, marginLeft: 12, marginRight: 12 }}
                      />
                      <Text type="secondary">{item.count}</Text>
                    </div>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Thông tin đồng bộ" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text type="secondary">Lần đồng bộ cuối: </Text>
                  <Text strong>
                    {overview.lastSyncAt 
                      ? new Date(overview.lastSyncAt).toLocaleString('vi-VN')
                      : 'Chưa đồng bộ'
                    }
                  </Text>
                </div>
                <div>
                  <Text type="secondary">Records có điểm: </Text>
                  <Text strong>{overview.recordsWithGrades}</Text>
                </div>
                <div>
                  <Text type="secondary">Records không đạt: </Text>
                  <Text strong style={{ color: overview.failedRecords > 0 ? '#ff4d4f' : '#52c41a' }}>
                    {overview.failedRecords}
                  </Text>
                </div>
                <div>
                  <Text type="secondary">Điểm TB chung: </Text>
                  <Text strong>
                    {overview.avgGrade ? overview.avgGrade.toFixed(2) : 'N/A'}
                  </Text>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>
          <DatabaseOutlined style={{ marginRight: 12 }} />
          Đồng bộ điểm từ CSDL phụ
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadData}>
            Làm mới
          </Button>
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={handleSyncAll}
            disabled={syncAllProgress !== null || syncingTable !== null}
          >
            Đồng bộ tất cả
          </Button>
        </Space>
      </div>

      {syncAllProgress && (
        <Alert
          type="info"
          showIcon
          icon={<SyncOutlined spin />}
          message="Đang đồng bộ..."
          description={
            <Progress 
              percent={Math.round(syncAllProgress.current / syncAllProgress.total * 100)} 
              status="active"
            />
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Tabs defaultActiveKey="1">
        <TabPane tab="Thống kê" key="1">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 50 }}>
              <Spin size="large" />
            </div>
          ) : (
            renderStatistics()
          )}
        </TabPane>

        <TabPane tab="Bảng điểm" key="2">
          <Card 
            title={`Danh sách bảng điểm (${tables.length})`}
            extra={
              <Space>
                <Input.Search
                  placeholder="Tìm sinh viên..."
                  value={studentMaHS}
                  onChange={(e) => setStudentMaHS(e.target.value)}
                  onSearch={handleSyncStudent}
                  loading={studentSyncing}
                  style={{ width: 250 }}
                  enterButton={
                    <Button type="primary" icon={<SyncOutlined />} loading={studentSyncing}>
                      Sync
                    </Button>
                  }
                />
              </Space>
            }
          >
            <Table
              dataSource={tables}
              columns={tableColumns}
              rowKey="tableName"
              pagination={{ pageSize: 10 }}
              loading={loading}
              size="small"
            />
          </Card>
        </TabPane>

        <TabPane tab="Kết quả đồng bộ" key="3">
          <Card title="Lịch sử đồng bộ">
            {syncResults.length > 0 ? (
              <Table
                dataSource={syncResults}
                columns={resultColumns}
                rowKey={(record, index) => `${record.khoaHocId}-${record.syncTime}-${index}`}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            ) : (
              <div style={{ textAlign: 'center', padding: 50, color: '#8c8c8c' }}>
                Chưa có kết quả đồng bộ
              </div>
            )}
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default GradeSyncPage;

